import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./ContactForm.css";

const ENDPOINT = "/api/contacto";
const ESPERA_S = 60; // pausa entre envíos desde el mismo navegador
const CLAVE_ULTIMO_ENVIO = "lastSubmitTime";

const VACIO = { nombre: "", correo: "", telefono: "", mensaje: "", consentimiento: false, web: "" };

const segundosRestantes = () => {
    const ultimo = Number(localStorage.getItem(CLAVE_ULTIMO_ENVIO)) || 0;
    return Math.max(0, Math.ceil((ultimo + ESPERA_S * 1000 - Date.now()) / 1000));
};

const ContactForm = () => {
    const [formData, setFormData] = useState(VACIO);
    const [enviando, setEnviando] = useState(false);
    const [mensaje, setMensaje] = useState(null); // { tipo: "ok" | "error", texto }
    const [espera, setEspera] = useState(segundosRestantes);
    const enviandoRef = useRef(false);

    // Cuenta atrás; el intervalo se limpia siempre al desmontar o al terminar.
    useEffect(() => {
        if (espera <= 0) return undefined;
        const id = setInterval(() => setEspera(segundosRestantes()), 1000);
        return () => clearInterval(id);
    }, [espera]);

    const handleChange = (e) => {
        const { name, type, value, checked } = e.target;
        setFormData((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
        if (mensaje && mensaje.tipo === "error") setMensaje(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (enviandoRef.current || espera > 0) return;

        enviandoRef.current = true;
        setEnviando(true);
        setMensaje(null);
        try {
            const response = await fetch(ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });
            let datos = {};
            try {
                datos = await response.json();
            } catch {
                /* respuesta vacía o no JSON */
            }
            if (!response.ok) {
                throw new Error(datos.error || "No se ha podido enviar la solicitud. Inténtalo más tarde.");
            }
            setMensaje({ tipo: "ok", texto: datos.message || "Solicitud enviada correctamente." });
            setFormData(VACIO);
            localStorage.setItem(CLAVE_ULTIMO_ENVIO, String(Date.now()));
            setEspera(segundosRestantes());
        } catch (error) {
            setMensaje({
                tipo: "error",
                texto:
                    error instanceof TypeError
                        ? "Sin conexión. Comprueba tu red e inténtalo de nuevo."
                        : error.message,
            });
        } finally {
            enviandoRef.current = false;
            setEnviando(false);
        }
    };

    let textoBoton = "Enviar solicitud";
    if (enviando) textoBoton = "Enviando…";
    else if (espera > 0) textoBoton = `Espera ${espera} s`;

    return (
        <section className="contact-form">
            <h2>Solicita presupuesto</h2>
            <form onSubmit={handleSubmit}>
                <label className="cf-campo">
                    <span>Nombre</span>
                    <input
                        type="text"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleChange}
                        maxLength={100}
                        autoComplete="name"
                        required
                    />
                </label>
                <label className="cf-campo">
                    <span>Correo electrónico</span>
                    <input
                        type="email"
                        name="correo"
                        value={formData.correo}
                        onChange={handleChange}
                        maxLength={200}
                        autoComplete="email"
                        required
                    />
                </label>
                <label className="cf-campo">
                    <span>
                        Teléfono <small>(opcional)</small>
                    </span>
                    <input
                        type="tel"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleChange}
                        maxLength={30}
                        autoComplete="tel"
                    />
                </label>
                <label className="cf-campo">
                    <span>¿Qué necesitas transportar?</span>
                    <textarea
                        name="mensaje"
                        value={formData.mensaje}
                        onChange={handleChange}
                        maxLength={5000}
                        required
                    />
                </label>

                {/* Campo trampa para bots: oculto a personas y lectores de pantalla */}
                <div className="cf-trampa" aria-hidden="true">
                    <label>
                        No rellenar
                        <input
                            type="text"
                            name="web"
                            value={formData.web}
                            onChange={handleChange}
                            tabIndex={-1}
                            autoComplete="off"
                        />
                    </label>
                </div>

                <label className="cf-consentimiento">
                    <input
                        type="checkbox"
                        name="consentimiento"
                        checked={formData.consentimiento}
                        onChange={handleChange}
                        required
                    />
                    <span>
                        He leído y acepto la <Link to="/politica-privacidad">política de privacidad</Link>.
                        Usaremos tus datos solo para responder a tu solicitud.
                    </span>
                </label>

                <button type="submit" disabled={enviando || espera > 0}>
                    {textoBoton}
                </button>
            </form>
            {mensaje && (
                <p className={`cf-mensaje cf-mensaje--${mensaje.tipo}`} role={mensaje.tipo === "error" ? "alert" : "status"}>
                    {mensaje.texto}
                </p>
            )}
        </section>
    );
};

export default ContactForm;
